import React, { useEffect, useState, useContext, useMemo } from "react";
import EventDetails from "./eventDetails/EventDetails";
import EventTiming from "./eventTiming/EventTiming";
import StudentDetails from "./studentDetails/StudentDetails";
import EventLocation from "./eventLocation/EventLocation";
import ConfirmationStep from "./confirmation/ConfirmationStep";
import MultiDayPlanner from "./multiDay/MultiDayPlanner"; // ← new import
import { motion, AnimatePresence } from "framer-motion";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import moment from "moment";
import appContext from "../../../context/app/appContext";
import { useNavigate } from "react-router-dom";
import LoadingComponent from "../../global/LoadingComponent";
import { FiX } from "react-icons/fi";
import { FaClipboardCheck } from "react-icons/fa";

const baseSteps = [
  { name: "Event Details", component: EventDetails },
  { name: "Dates and Times", component: EventTiming },
  { name: "Locations", component: EventLocation },
  { name: "Student Details", component: StudentDetails },
];

const Confirmation = { name: "Confirmation", component: ConfirmationStep };

const deepEqual = (a, b) => {
  return JSON.stringify(a) === JSON.stringify(b);
};

const defaultFormData = {
  eventName: "",
  eventDescription: "",

  locations: [
    {
      address:
        "Redeemer Lutheran College, Rochedale Road, Rochedale QLD, Australia",
      offSite: false,
      interState: false,
      overSeas: false,
      mode: "search",
    },
  ],
  roomResource: "",
  preferredDate: "",
  startTime: "",
  overnight: "",
  endDate: "",
  endTime: "",
  eventRecurs: "",
  recurrence: "",
  recurrenceEnd: "",
  recurOverHolidays: false,
  category: "",
  students: {
    type: "studentGroups",
    selectedGroups: [],
    groupStudentsMap: {},
    studentMap: {},
    customStudents: [],
    noStudentsAttending: false,
    notYetConfirmed: false,
    notYetConfirmedDetails: {},
  },
  staff: {
    organiser: null,
    attending: [],
    staffUnknown: false,
    staffUnknownDetails: {
      approxNumStaff: "",
      staffDescription: "",
    },
  },
  transport: {
    required: false,
    modes: {
      bus: false,
      taxi: false,
      air: false,
    },
  },
  parentConsentRequired: false,
  rollsMarked: false,
  dateFlexibility: false,
  dateChangeReason: "",
  otherDetails: "",
  externalProvidersUsed: false,
  tags: [],
  multiDays: [],
  isMultiDay: false,
};

const EventBookingForm = ({
  formData: initialFormData,
  setFormData: setExternalFormData,
  editable = true,
  isEdit = false,
  event_id = null,
  hasProgressed,
}) => {
  const { serverPost, dbUser } = useContext(appContext);
  const [formData, setFormData] = useState(initialFormData || defaultFormData);

  const [hasChanged, setHasChanged] = useState(false);
  const [initialSnapshot, setInitialSnapshot] = useState(null);

  const [submitting, setSubmitting] = useState(false);

  const [changeReason, setChangeReason] = useState("");

  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(1);

  // Sync external formData into internal state (e.g. from backend fetch)
  useEffect(() => {
    if (initialFormData) {
      setFormData(initialFormData);
      setInitialSnapshot(initialFormData);
    }
    // eslint-disable-next-line
  }, [initialFormData]);

  useEffect(() => {
    if (isEdit && initialSnapshot) {
      setHasChanged(!deepEqual(formData, initialSnapshot));
    }
    // eslint-disable-next-line
  }, [formData, initialSnapshot, isEdit]);

  // Optionally sync internal changes to parent
  useEffect(() => {
    if (setExternalFormData) {
      setExternalFormData(formData);
    }
    // eslint-disable-next-line
  }, []);

  const steps = useMemo(() => {
    const base = [...baseSteps];
    if (formData?.isMultiDay) {
      base.push({ name: "Multi-Day Planner", component: MultiDayPlanner });
    }
    base.push(Confirmation);
    return base;
  }, [formData?.isMultiDay]);

  const StepComponent = steps[currentStep].component;

  const handleChange = (field, value) => {
    if (!editable) return;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    if (!isStepComplete(currentStep)) return;
    setDirection(1);
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    setDirection(-1);
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const route = isEdit ? `/events/update/${event_id}` : `/events/create`;

      const res = await serverPost(route, {
        data: formData,
        submitterId: dbUser.staffEmpCode,
        submitter: `${dbUser.staffGvnName} ${dbUser.staffSurname}`,
        submitterEmail: dbUser.staffEmail,
        hasProgressed: hasProgressed,
        changeReason: changeReason,
      });

      console.log(res);

      if (res.status === "success") {
        navigate(`/viewEvent/${res.event_id}`);
      } else {
        alert("There was an error submitting the event.");
      }
    } catch (error) {
      console.error("Submit failed:", error);
    }
    setSubmitting(false);
  };

  const isStepComplete = (index) => {
    const step = steps[index].name;
    return getIncompleteFields(step).length === 0;
  };

  const getIncompleteFields = (step) => {
    const pending = [];
    const { staff, students } = formData;

    if (step === "Multi-Day Planner") {
      const pending = [];

      if (!formData.isMultiDay) return [];

      const days = formData.multiDays || [];

      if (days.length === 0) {
        return ["At least one day must be added to the planner"];
      }

      const firstDay = days[0];
      const hasAnyInput = [
        firstDay.startTime,
        firstDay.endTime,
        firstDay.activity,
        ...(firstDay.locations || []),
        ...(firstDay.staff || []),
        ...(firstDay.students || []),
      ].some((v) => (Array.isArray(v) ? v.length > 0 : !!v));

      if (!hasAnyInput) {
        return ["Multi-Day Planner has not been filled out yet"];
      }

      days.forEach((day) => {
        const label = moment(day.date).format("ddd, DD MMM");

        if (!day.startTime) pending.push(`${label}: Missing start time`);
        if (!day.endTime) {
          pending.push(`${label}: Missing end time`);
        } else {
          const start = moment(day.startTime, "HH:mm", true);
          const end = moment(day.endTime, "HH:mm", true);
          if (start.isValid() && end.isValid() && end.isSameOrBefore(start)) {
            pending.push(`${label}: End time must be after start time`);
          }
        }

        if (!day.activity?.trim()) pending.push(`${label}: Missing activity`);
        if (!Array.isArray(day.locations) || day.locations.length === 0) {
          pending.push(`${label}: No locations selected`);
        }

        const staffConfirmed = !formData.staff.staffUnknown;
        const studentsConfirmed =
          !formData.students.notYetConfirmed &&
          !formData.students.noStudentsAttending;

        if (staffConfirmed && (!day.staff || day.staff.length === 0)) {
          pending.push(`${label}: No staff selected`);
        }

        if (studentsConfirmed && (!day.students || day.students.length === 0)) {
          pending.push(`${label}: No students selected`);
        }
      });

      return pending;
    }

    if (step === "Event Details") {
      const pending = [];

      const { staff } = formData;

      if (!formData.eventName?.trim()) pending.push("Event Name");
      if (!formData.eventDescription?.trim()) pending.push("Description");
      if (!formData.category) pending.push("Category");
      if (!staff.organiser?.value) pending.push("Organiser");

      const approxNum = parseInt(
        staff.staffUnknownDetails?.approxNumStaff || 0
      );
      const description = (
        staff.staffUnknownDetails?.staffDescription || ""
      ).trim();

      const hasKnownStaff =
        !staff.staffUnknown &&
        Array.isArray(staff.attending) &&
        staff.attending.length > 0;

      const usingUnknownStaff = staff.staffUnknown === true;
      const hasUnknownEstimate = approxNum > 0;
      const hasUnknownDescription = description.length > 0;

      // ✅ Only push "Staff in Attendance" if neither method is being used at all
      if (!hasKnownStaff && !usingUnknownStaff) {
        pending.push("Staff in Attendance");
      }

      // ✅ If using unknown staff, validate fields separately (but don’t double up with above)
      if (usingUnknownStaff) {
        if (!hasUnknownEstimate) {
          pending.push("Approximate Number of Staff");
        }
        if (!hasUnknownDescription) {
          pending.push("Staff Description");
        }
      }

      return pending;
    }

    if (step === "Locations") {
      if (
        !Array.isArray(formData.locations) ||
        formData.locations.length === 0 ||
        formData.locations.some((l) => !l.address?.trim())
      ) {
        pending.push("Valid Location");
      }

      const isHome = formData.locations.some(
        (l) =>
          l.address?.toLowerCase().includes("redeemer lutheran college") &&
          l.address?.toLowerCase().includes("qld")
      );

      if (isHome && !formData.roomResource?.trim()) {
        pending.push("Rooms/Resources required");
      }

      return pending;
    }

    if (step === "Dates and Times") {
      const {
        preferredDate,
        startTime,
        endDate,
        endTime,
        recurrence,
        recurrenceEnd,
        eventRecurs,
        dateFlexibility,
        dateChangeReason,
        overnight,
      } = formData;

      if (!preferredDate) pending.push("Start Date");
      if (!startTime) pending.push("Start Time");
      if (!endDate && overnight) pending.push("End Date");
      if (!endTime) pending.push("End Time");

      if (eventRecurs) {
        if (!recurrence?.trim()) pending.push("Recurrence Pattern");
        if (!recurrenceEnd?.trim()) pending.push("Recurrence End Date");
      }

      if (dateFlexibility) {
        if (!dateChangeReason?.trim()) {
          pending.push("Date Change Reason is required");
        }
      }

      const start =
        preferredDate && startTime
          ? moment(`${preferredDate} ${startTime}`, "YYYY-MM-DD HH:mm")
          : null;

      let end = null;
      if ((overnight && endDate && endTime) || (!overnight && endTime)) {
        const effectiveEndDate = overnight ? endDate : preferredDate;
        if (effectiveEndDate && endTime) {
          end = moment(`${effectiveEndDate} ${endTime}`, "YYYY-MM-DD HH:mm");
        }
      }

      const recurrenceEndDate =
        recurrenceEnd && moment(recurrenceEnd, "YYYY-MM-DD").isValid()
          ? moment(recurrenceEnd, "YYYY-MM-DD")
          : null;

      if (start && start.isBefore(moment())) {
        pending.push("Start date is in the past");
      }

      if (start && end && end.isSameOrBefore(start)) {
        pending.push("End date is before start");
      }

      if (
        eventRecurs &&
        recurrenceEndDate &&
        start &&
        recurrenceEndDate.isSameOrBefore(start)
      ) {
        pending.push("Recurrence ends before start");
      }

      return pending;
    }

    if (step === "Student Details") {
      if (students.noStudentsAttending) return [];

      const studentCount = Object.keys(students.studentMap || {}).length;
      const approxStudents = parseInt(
        students.notYetConfirmedDetails?.approxNumStudents || 0
      );

      const hasData = students.notYetConfirmed
        ? approxStudents > 0
        : studentCount > 0;

      const hasEstimate =
        staff.staffUnknown &&
        parseInt(staff.staffUnknownDetails?.approxNumStaff || 0) > 0;

      const hasAssigned =
        !staff.staffUnknown &&
        Array.isArray(staff.attending) &&
        staff.attending.length > 0;

      if (!hasData) pending.push("Student Attendance");
      if (!(hasEstimate || hasAssigned)) pending.push("Staff Supervision");

      return pending;
    }

    if (step === "Confirmation") {
      const pending = [];

      if (isEdit && !changeReason.trim()) {
        pending.push("Reason for changes");
      }

      return pending;
    }

    return [];
  };

  const variants = {
    enter: (dir) => ({
      x: dir > 0 ? 300 : -300,
      opacity: 0,
      position: "absolute",
    }),
    center: {
      x: 0,
      opacity: 1,
      position: "relative",
    },
    exit: (dir) => ({
      x: dir > 0 ? -300 : 300,
      opacity: 0,
      position: "absolute",
    }),
  };

  return (
    <div className={`px-4 sm:px-6 lg:px-12 xl:px-0 flex justify-center `}>
      {submitting && <LoadingComponent message="Submitting form..." />}
      <div
        className={`w-full max-w-4xl shadow-xl rounded-2xl p-6 sm:p-8 ${
          isEdit ? "bg-amber-100" : ""
        } `}
      >
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl italic md:text-3xl font-bold text-gray-800">
            {isEdit ? "Edit event proposal" : "New Event Proposal"}
          </h1>
          {isEdit ? (
            <div className="flex justify-end ">
              <button
                onClick={() => navigate(`/viewEvent/${event_id}`)}
                className="flex items-center gap-2 text-sm bg-red-100 text-red-700 hover:bg-red-200 px-2 py-1.5 rounded-md transition"
              >
                <FiX size={16} />
                Cancel Edit
              </button>
            </div>
          ) : (
            <img src="/Go.png" alt="Go Logo" className="h-28 object-contain" />
          )}
        </div>
        {isEdit && !hasChanged && (
          <div className="mb-4">
            <div className="flex items-start gap-3 bg-gray-100 border-l-4 border-gray-300 text-gray-700 px-4 py-2 rounded-md text-sm">
              <span className="font-medium">Awaiting changes:</span>
              <span>
                No modifications detected yet. Make a change to enable
                submission.
              </span>
            </div>
          </div>
        )}
        {hasProgressed && (
          <div className="mb-6">
            <div className="flex items-start gap-3 bg-pink-50 border-l-4 border-pink-400 text-pink-800 px-4 py-3 rounded-md">
              <FaClipboardCheck className="mt-1 text-pink-500" />
              <div className="text-sm">
                <p className="font-semibold">Approvals in progress</p>
                <p>
                  This event proposal has already progressed in the approval
                  workflow. The approval process will remain paused while
                  changes are assessed.
                </p>
              </div>
            </div>
          </div>
        )}
        {/* Step indicators */}
        <div className="flex items-start justify-center gap-6 mb-6 flex-wrap">
          {steps.map((step, i) => {
            const incomplete = getIncompleteFields(step.name);
            const isCurrent = i === currentStep;

            const isConfirmation = step.name === "Confirmation";
            const isComplete = isConfirmation
              ? isEdit && incomplete.length === 0 // only complete if edit + reason provided
              : incomplete.length === 0;

            const allPrevStepsValid = steps
              .slice(0, i)
              .every((_, idx) => isStepComplete(idx));

            const isClickable =
              isCurrent ||
              (!isConfirmation && allPrevStepsValid) ||
              (isConfirmation && allPrevStepsValid);

            const circleColor = isConfirmation
              ? "bg-gray-300"
              : isComplete
              ? "bg-green-500"
              : isCurrent
              ? "bg-blue-600"
              : "bg-gray-300";

            return (
              <div
                key={step.name}
                className={`flex flex-col items-center min-w-[100px] text-center ${
                  isClickable ? "cursor-pointer" : "cursor-not-allowed"
                }`}
                onClick={() => {
                  if (isClickable) {
                    setDirection(i > currentStep ? 1 : -1);
                    setCurrentStep(i);
                  }
                }}
              >
                <div className={`w-4 h-4 rounded-full ${circleColor}`} />
                <span
                  className={`text-sm ${
                    isCurrent
                      ? "font-semibold text-blue-700"
                      : isClickable
                      ? "text-gray-600 hover:underline"
                      : "text-gray-400"
                  }`}
                >
                  {step.name}
                </span>

                {/* Status text below */}
                {isComplete ? (
                  <span className="text-xs text-green-600 mt-1">
                    ✓ Step complete
                  </span>
                ) : incomplete.length > 0 ? (
                  <span className="text-xs text-yellow-600 mt-1">
                    • {incomplete.length} item{incomplete.length > 1 ? "s" : ""}{" "}
                    remaining
                  </span>
                ) : (
                  <span className="text-xs text-transparent mt-1">padding</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Animated step content */}
        <div className="relative min-h-[300px] overflow-visible">
          <AnimatePresence custom={direction} mode="wait">
            <motion.div
              key={currentStep}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="w-full"
            >
              <StepComponent
                data={formData}
                onChange={handleChange}
                isEdit={isEdit}
                setChangeReason={setChangeReason}
                changeReason={changeReason}
                onJumpToStep={(index) => {
                  setDirection(index > currentStep ? 1 : -1);
                  setCurrentStep(index);
                }}
                editable={editable}
                hasChanged={hasChanged}
              />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Navigation */}
        <div className="flex justify-between pt-6">
          <button
            onClick={handleBack}
            disabled={currentStep === 0}
            className="flex items-center gap-2 bg-gray-200 text-gray-800 px-5 py-2 rounded-md hover:bg-gray-300"
          >
            <FaChevronLeft />
            Back
          </button>

          {currentStep < steps.length - 1 ? (
            <button
              onClick={handleNext}
              disabled={!isStepComplete(currentStep)}
              className={`flex items-center gap-2 px-6 py-2 rounded-md transition-colors duration-300 ${
                isStepComplete(currentStep)
                  ? "bg-blue-600 text-white hover:bg-blue-700"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              Next
              <FaChevronRight />
            </button>
          ) : (
            editable && (
              <button
                onClick={handleSubmit}
                disabled={
                  !isStepComplete(currentStep) || (isEdit && !hasChanged)
                }
                className={`flex items-center gap-2 px-6 py-2 rounded-md transition-colors duration-300 ${
                  isStepComplete(currentStep) && (!isEdit || hasChanged)
                    ? "bg-green-600 text-white hover:bg-green-700"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                Submit
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default EventBookingForm;
